import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import {
  ContractCommercialMeteringPoint,
  ContractInfo,
  ContractOrganization,
} from '@/features/contracts/types';
import { FiltersGrid, Pagination } from '@/features/types';
import {
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export const contractsApi = createApi({
  reducerPath: 'contractsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/contracts`,
  }),
  tagTypes: ['Contract', 'Contracts', 'ContractCommercialMeteringPoints'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getContracts: builder.infiniteQuery<
      ContractOrganization[],
      FiltersGrid & { counterpartyAccountId: string; name?: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...pageParam, ...queryArg },
      }),
      providesTags: () => ['Contracts'],
    }),
    getContractById: builder.query<ContractInfo, string>({
      query: id => ({ url: `/${id}` }),
      providesTags: ['Contract'],
    }),
    getContractCommercialMeteringPointsById: builder.infiniteQuery<
      ContractCommercialMeteringPoint[],
      FiltersGrid & { contractId: string; name?: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/${queryArg.contractId}/commercial-metering-points`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: ['ContractCommercialMeteringPoints'],
    }),
  }),
});

export const { useGetContractByIdQuery, useLazyGetContractByIdQuery } =
  contractsApi;

export const useGetContractCommercialMeteringPointsByIdInfiniteQuery =
  normalizeInfiniteQueryData(
    contractsApi.useGetContractCommercialMeteringPointsByIdInfiniteQuery,
  );

export const useGetContractsInfiniteQuery = normalizeInfiniteQueryData(
  contractsApi.useGetContractsInfiniteQuery,
);
